import { createRouter, createWebHistory } from 'vue-router'
import { storeToRefs } from 'pinia'
import AppLayout from '@/layout/AppLayout.vue';
import AuthLayout from '@/layout/AuthLayout.vue';
import { useAuthStore, useAppErrorStore } from '@/stores'
import errorRoutes from './errors';
// import { ability } from '../services/ability';

const REDIRECT_AWAY_IF_LOGGED_IN = [
  'login',
  'forgot-password',
]

// Routes with this meta are restricted to users holding one of the listed roles.
// Routes without `allowedRoles` are open to any authenticated user (i.e. DGs see them too).
const managerOnly = { allowedRoles: ['admin', 'dc'] }

const dashboardRoutes = [
  {
    path: 'tours',
    component: () => import('@/views/dashboard/Tours.vue'),
  },
  {
    path: 'tasks',
    component: () => import('@/views/dashboard/Tasks.vue'),
  },
  {
    path: 'calendar',
    component: () => import('@/views/dashboard/Calendar.vue'),
  },
  {
    path: 'docs',
    component: () => import('@/views/dashboard/Docs.vue'),
  },  
]

const routes = [
  {
    path: '/',
    // redirect: '/login',
    component: AppLayout,
    meta: { requiresAuth: true },
    children: [
      {
        // The Dashboard is the default/root path
        path: '',
        component: () => import('@/views/dashboard/DashboardPage.vue'),
        children: [
          {
            path: '',
            name: 'Dashboard',
            component: () => import('@/views/dashboard/DashboardHome.vue'),
          },
          ...dashboardRoutes
        ]
      },
      {
        path: '/user/:userId',
        component: () => import('@/views/dashboard/DashboardPage.vue'),
        children: [
          {
            path: '',
            component: () => import('@/views/dashboard/Tours.vue'),
            // redirect: to => {
            //   return `/user/${to.params.userId}/tours`
            // },
          },
          ...dashboardRoutes   
        ]
      },
      {
        path: '/transferees/:id',
        component: () => import('@/views/transferees/Page.vue'),
        children: [
          {
            path: '',
            name: 'showTransferee',
            component: () => import('@/views/transferees/Show.vue'),
          },
          {
            path: 'comments',
            name: 'commentsTransferee',
            component: () => import('@/views/transferees/CommentsTab.vue'),
          },
          {
            path: 'edit',
            name: 'editTransferee',
            component: () => import('@/views/transferees/Edit.vue'),
            meta: managerOnly,
          },
          {
            path: 'escrow_worksheet',
            name: 'escrowWorksheetTransferee',
            component: () => import('@/views/escrow_worksheets/Page.vue'),
            meta: managerOnly,
          },
          {
            path: 'documents',
            name: 'documentsTransferee',
            component: () => import('@/views/transferees/Files.vue'),
            meta: managerOnly,
          },
          {
            path: 'close_email',
            name: 'closeEmailTransferee',
            component: () => import('@/views/emails/close/CloseEmailPage.vue'),
            meta: managerOnly,
          },
          {
            path: 'invoices',
            name: 'invoicesTransferee',
            component: () => import('@/views/transferees/Invoices.vue'),
            meta: managerOnly,
          },
          {
            path: 'tours/new',
            name: 'newTour',
            component: () => import('@/views/tours/New.vue'),
            meta: managerOnly,
          },

        ]        
      },
      {
        path: '/tours/:tourId',
        component: () => import('@/views/tours/Page.vue'),
        children: [
          {
            path: '',
            name: 'showTour',
            component: () => import('@/views/tours/Show.vue'),
          },
          {
            path: 'comments',
            name: 'commentsTour',
            component: () => import('@/views/tours/CommentsTab.vue'),
          },
          {
            path: 'edit',
            name: 'editTour',
            component: () => import('@/views/tours/Edit.vue'),
            meta: managerOnly,
          },
          {
            path: 'timesheet',
            name: 'timesheetTour',
            component: () => import('@/views/tours/TimesheetTab.vue'),
            meta: managerOnly,
          },
          {
            path: 'follow-up',
            name: 'followUpTour',
            component: () => import('@/views/tours/FollowUpTab.vue'),
          },
          {
            path: 'schedule-email',
            name: 'scheduleEmailTour',
            component: () => import('@/views/emails/schedule/ScheduleEmailPage.vue'),
            meta: managerOnly,
          },                   
        ]        
      }, 
      {
        path: '/admin/companies',
        name: 'companiesIndex',
        component: () => import('@/views/admin/companies/Index.vue'),
        meta: managerOnly,
      },
      {
        path: '/admin/companies/:companyId',
        component: () => import('@/views/admin/companies/Page.vue'),
        meta: managerOnly,
        children: [
          {
            path: '',
            name: 'companyShow',
            component: () => import('@/views/admin/companies/Show.vue'),
          },
          {
            path: 'edit',
            name: 'companyEdit',
            component: () => import('@/views/admin/companies/Edit.vue'),
          },
          {
            path: 'files',
            name: 'companyFiles',
            component: () => import('@/views/admin/companies/Files.vue'),
          },
        ]
      },
      {
        path: '/admin/relo-companies',
        name: 'reloCompaniesIndex',
        component: () => import('@/views/admin/relo_companies/Index.vue'),
        meta: managerOnly,
      },      
      {
        path: '/admin/relo-companies/:reloCompanyId',
        component: () => import('@/views/admin/relo_companies/Page.vue'),
        meta: managerOnly,
        children: [
          {
            path: '',
            name: 'reloCompanyShow',
            component: () => import('@/views/admin/relo_companies/Show.vue'),
          },
          {
            path: 'files',
            name: 'reloCompanyFiles',
            component: () => import('@/views/admin/relo_companies/Files.vue'),
          },
        ]
      },
      {
        path: '/reports/client-billing',
        name: 'ClientBilling',
        component: () => import('@/views/reports/ClientBilling.vue'),
        meta: managerOnly,
      },
      {
        path: '/reports/tour-history',
        name: 'TourHistory',
        component: () => import('@/views/reports/TourHistory.vue'),
        meta: managerOnly,
      },
      {
        path: '/reports/tour-count-by-person',
        name: 'TourCountByPerson',
        component: () => import('@/views/reports/TourCountByPerson.vue'),
        meta: managerOnly,
      },
      {
        path: '/reports/conversions',
        name: 'Conversions',
        component: () => import('@/views/reports/Conversions.vue'),
        meta: managerOnly,
      },
      {
        path: '/reports/approved-timesheets',
        name: 'approvedTimesheets',
        component: () => import('@/views/reports/ApprovedTimesheets.vue'),
        meta: managerOnly,
      },
      {
        path: '/reports/dc-initiations',
        name: 'dcInitiations',
        component: () => import('@/views/reports/DcInitiations.vue'),
        meta: managerOnly,
      },
      {
        path: '/reports/tour-feedback',
        name: 'tourFeedback',
        component: () => import('@/views/reports/TourFeedback.vue'),
        meta: managerOnly,
      },
      {
        path: '/reports/email-previews',
        name: 'emailPreviews',
        component: () => import('@/views/reports/EmailPreviews.vue'),
        meta: managerOnly,
      },
      {
        path: '/search',
        name: 'search',
        component: () => import('@/views/search/Search.vue'),
        meta: managerOnly,
      },
      {
        path: '/schedule',
        name: 'schedule',
        component: () => import('@/views/schedule/Schedule.vue'),
        meta: managerOnly,
      },
      {
        path: '/admin/users',
        name: 'adminUsers',
        component: () => import('@/views/admin/users/Index.vue'),
        meta: managerOnly,
      },
      {
        path: '/admin/users/:userId',
        component: () => import('@/views/admin/users/Page.vue'),
        meta: managerOnly,
        children: [
          {
            path: '',
            name: 'userShow',
            component: () => import('@/views/admin/users/Show.vue'),
          },
          {
            path: 'edit',
            name: 'userEdit',
            component: () => import('@/views/admin/users/Edit.vue'),
            meta: { allowedRoles: ['admin'] },
          },
          {
            path: 'files',
            name: 'userFiles',
            component: () => import('@/views/admin/users/Files.vue'),
          },
        ]
      },
      {
        path: '/admin/counselors',
        name: 'adminCounselors',
        component: () => import('@/views/admin/destination_counselors/Index.vue'),
        meta: managerOnly,
      },
      {
        path: '/admin/relo-consultants',
        name: 'adminReloConsultants',
        component: () => import('@/views/admin/relo_consultants/Index.vue'),
        meta: managerOnly,
      },
      {
        path: '/admin/hr-contacts',
        name: 'adminHrContacts',
        component: () => import('@/views/admin/hr_contacts/Index.vue'),
        meta: managerOnly,
      },
      {
        path: '/admin/guides',
        name: 'adminGuides',
        component: () => import('@/views/admin/destination_guides/Index.vue'),
        meta: managerOnly,
      },
      {
        path: '/admin/lenders',
        name: 'adminLenders',
        component: () => import('@/views/admin/lenders/Index.vue'),
        meta: managerOnly,
      },
      {
        path: '/admin/escrow-officers',
        name: 'adminEscrowOfficers',
        component: () => import('@/views/admin/escrow_officers/Index.vue'),
        meta: managerOnly,
      },
      {
        path: '/admin/lender-contacts',
        name: 'adminLenderContacts',
        component: () => import('@/views/admin/lender_contacts/Index.vue'),
        meta: managerOnly,
      },
      {
        path: '/admin/realtors',
        name: 'adminRealtors',
        component: () => import('@/views/admin/realtors/Index.vue'),
        meta: managerOnly,
      },
      {
        path: '/admin/realtors/:realtorId',
        component: () => import('@/views/admin/realtors/Page.vue'),
        meta: managerOnly,
        children: [
          {
            path: '',
            name: 'adminRealtorShow',
            component: () => import('@/views/admin/realtors/Show.vue'),
          },
          {
            path: 'files',
            name: 'adminRealtorFiles',
            component: () => import('@/views/admin/realtors/Files.vue'),
          },
        ],
      },
      {
        path: '/settings',
        name: 'userSettings',
        component: () => import('@/views/users/UserSettings.vue'),
      },
      {
        path: '/test',
        name: 'test',
        component: () => import('@/views/Test.vue'),
      },
    ],
  },
  {
    path: '/',
    component: AuthLayout,
    children: [
      {
        path: '/login',
        name: 'login',
        component: () => import('@/views/auth/Login.vue'),
      },
      {
        path: '/forgot-password',
        name: 'forgot-password',
        component: () => import('@/views/auth/ForgotPassword.vue'),
      },
      {
        path: '/reset-password/:userId',
        name: 'reset-password',
        component: () => import('@/views/auth/ResetPassword.vue'),
      },
      {
        path: '/terms-of-service',
        name: 'terms-of-service',
        component: () => import('@/views/auth/TermsOfService.vue'),
      },
      {
        path: '/privacy-policy',
        name: 'privacy-policy',
        component: () => import('@/views/auth/PrivacyPolicy.vue'),
      },
    ],
  },
  ...errorRoutes,
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  // history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    // return desired position
    return { left: 0, top: 0 };
  }
})

// Reactive refs(ref and computed properties) should be destrctured like following to mainten reactivity
  // const { token } = storeToRefs(authStore)
// the action can just be destructured like
  // const { login } = authStore

router.beforeEach((to, _, next) => {
  document.title = to.meta?.title ?? 'TRMG'

  const requiresAuth = to.meta.requiresAuth;
  const allowedRoles = to.meta.allowedRoles;

  const authStore = useAuthStore()
  const { isLoggedIn, currentUser } = storeToRefs(authStore)

  if (requiresAuth && !isLoggedIn.value) {
    authStore.setRedirectUrl(to.path)
    next({ name: 'login' });
  } else if (
    !requiresAuth &&
    isLoggedIn.value &&
    REDIRECT_AWAY_IF_LOGGED_IN.includes(to.name)) {
      next({name: 'Dashboard'});
  } else if (requiresAuth && allowedRoles && !allowedRoles.some(role => currentUser.value.hasRole(role))) {
    next({ name: 'Unauthorized' });
  } else {
   next();
  }
})

router.afterEach((to) => {
  // clear error messages, if any
  const appErrorStore = useAppErrorStore()
  appErrorStore.reset()
})

export default router